// ============================================
// ROLC OneROLC - Queue Service (Offline Support)
// Version: 3.6.0
// ============================================

import { CONFIG } from '../config/constants.js';
import { showToast } from '../components/Toast.js';
import { apiCall } from '../api/apiClient.js';

const DB_NAME = CONFIG.DB_NAME || 'rolc_queue';
const STORE_NAME = CONFIG.STORE_NAME || 'submissions';

// ============================================
// IndexedDB Helpers
// ============================================
function openQueueDB() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = (e) => {
            const db = e.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
            }
        };
        req.onsuccess = (e) => resolve(e.target.result);
        req.onerror = (e) => reject(e);
    });
}

// ============================================
// Queue Operations
// ============================================
export async function queueSubmission(token, submissions, surveyName) {
    try {
        const db = await openQueueDB();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        
        await new Promise((resolve, reject) => {
            const req = store.add({
                token,
                submissions,
                surveyName,
                ts: Date.now()
            });
            req.onsuccess = () => resolve();
            req.onerror = () => reject(req.error);
        });
        db.close();
        
        showToast('Saved offline. Will auto-send when online.', 'info');
        await updateQueueBadge();
        return true;
    } catch (e) {
        console.error('Queue failed:', e);
        showToast('Unable to save offline. Please try again.', 'error');
        return false;
    }
}

export async function getQueuedCount() {
    try {
        const db = await openQueueDB();
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);
        
        const count = await new Promise((resolve) => {
            const req = store.count();
            req.onsuccess = () => resolve(req.result);
            req.onerror = () => resolve(0);
        });
        db.close();
        return count;
    } catch (e) {
        return 0;
    }
}

export async function flushQueue() {
    if (!navigator.onLine) return;
    
    let db;
    try {
        db = await openQueueDB();
    } catch (e) {
        return;
    }

    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const all = await new Promise((resolve) => {
        const req = store.getAll();
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => resolve([]);
    });
    db.close();

    if (!all || all.length === 0) return;

    let sent = 0;
    for (const item of all) {
        try {
            const result = await apiCall('submit', {
                token: item.token,
                submissions: item.submissions,
                surveyName: item.surveyName
            });
            
            if (result.error) {
                console.log('Queue item failed:', item.id, result.error);
                continue;
            }

            const db2 = await openQueueDB();
            const tx2 = db2.transaction(STORE_NAME, 'readwrite');
            const store2 = tx2.objectStore(STORE_NAME);
            await new Promise((resolve, reject) => {
                const req = store2.delete(item.id);
                req.onsuccess = () => resolve();
                req.onerror = () => reject(req.error);
            });
            db2.close();
            sent++;
        } catch (err) {
            console.log('Queue item failed, will retry:', item.id, err);
        }
    }

    if (sent > 0) {
        showToast(`${sent} offline submission${sent > 1 ? 's' : ''} sent!`, 'success');
        await updateQueueBadge();
    }
}

export async function updateQueueBadge() {
    try {
        const count = await getQueuedCount();
        let badge = document.getElementById('queueBadge');
        
        if (!badge) {
            badge = document.createElement('div');
            badge.id = 'queueBadge';
            badge.style.cssText = `
                position: fixed;
                top: 12px;
                right: 12px;
                z-index: 999;
                background: #f59e0b;
                color: #fff;
                padding: 6px 14px;
                border-radius: 20px;
                font-size: 0.8rem;
                font-weight: 700;
                display: none;
                box-shadow: 0 2px 8px rgba(0,0,0,0.2);
            `;
            document.body.appendChild(badge);
        }
        
        if (count > 0) {
            badge.textContent = `📤 ${count} pending`;
            badge.style.display = 'block';
        } else {
            badge.style.display = 'none';
        }
    } catch (e) {
        // Silently fail
    }
}


